import { DecorationSet, Decoration } from '@tiptap/pm/view';
import { getPath } from '../utils.js';
import { NodeControlWidget } from './NodeControlWidget.js';
import { NodeControlPluginKey, CustomTextSelection } from './NodeControlPlugin.js';
import { ContextualMenuPluginKey } from '../ContextualMenu/ContextualMenuPlugin.js';
import { isDefined } from '@fabric-app/utils/guards';

const priorityNodeTypes = [
  "table"
];
class NodeControlState {
  static get empty() {
    return new NodeControlState(null, null, null, DecorationSet.empty, null);
  }
  static findValidNodePath(path) {
    let validNodePath = null;
    for (let i = 0; i < path.length - 1; i++) {
      const pathNode = path[i];
      if (priorityNodeTypes.includes(pathNode.node.type.name)) {
        validNodePath = pathNode;
      }
      if (!validNodePath && (!pathNode.node.inlineContent || pathNode.index > 0 || path.length <= 2)) {
        validNodePath = pathNode;
      }
    }
    return validNodePath;
  }
  constructor(nodePos, nodeStart, currentNodeElement, decorations, appliedWidgetAt) {
    this.nodePos = nodePos;
    this.nodeStart = nodeStart;
    this.currentNodeElement = currentNodeElement;
    this.decorations = decorations;
    this.appliedWidgetAt = appliedWidgetAt;
  }
  applyTransform(tr) {
    const { nodePos, nodeStart, currentNodeElement } = this;
    if (!tr.docChanged || !nodePos || !nodeStart || !currentNodeElement) {
      return this;
    }
    if (nodePos > tr.doc.content.size) {
      return NodeControlState.empty;
    }
    const path = getPath(tr.doc.resolve(nodePos));
    const foundNodePath = NodeControlState.findValidNodePath(path);
    if (!foundNodePath) {
      return NodeControlState.empty;
    }
    const element = tr.doc.nodeAt(foundNodePath.pos);
    if (!element || !(element instanceof HTMLElement)) {
      return NodeControlState.empty;
    }
    return new NodeControlState(foundNodePath.pos, foundNodePath.start, element, DecorationSet.empty, null);
  }
  applyMeta(meta) {
    return new NodeControlState(meta.nodePos, meta.nodeStart, meta.currentNodeElement, this.decorations, this.appliedWidgetAt);
  }
  applyWidgetDecoration(state) {
    const { nodePos, nodeStart, currentNodeElement, appliedWidgetAt } = this;
    if (!isDefined(nodePos) || !isDefined(nodeStart) || !isDefined(currentNodeElement)) {
      return NodeControlState.empty;
    }
    if (appliedWidgetAt === nodeStart) {
      return this;
    }
    return new NodeControlState(nodePos, nodeStart, currentNodeElement, DecorationSet.create(state.doc, [
      Decoration.widget(nodeStart, NodeControlWidget, {
        // @ts-expect-error - private property
        key: `node-controls-widget-${ContextualMenuPluginKey.key}-${NodeControlPluginKey.key}`,
        side: -1,
        destroy(node) {
          if (node instanceof HTMLElement && node.destroy) {
            node.destroy();
          }
        },
        ignoreSelection: true
      })
    ]), nodeStart);
  }
  applySelectionDecoration(tr) {
    const { decorations } = this;
    const selectionDecorations = decorations.find(void 0, void 0, (spec) => {
      return spec.key === "node-controls-selection";
    });
    if (tr.selectionSet) {
      if (tr.selection instanceof CustomTextSelection) {
        const { $from, $to } = tr.selection;
        const newDecorations = [];
        tr.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
          if (node.isBlock && pos >= $from.pos - 1 && pos <= $to.pos) {
            newDecorations.push(Decoration.node(pos, pos + node.nodeSize, {
              class: "ProseMirror-draghandle-selected"
            }, {
              key: "node-controls-selection"
            }));
            return false;
          }
          return true;
        });
        return new NodeControlState(this.nodePos, this.nodeStart, this.currentNodeElement, decorations.add(tr.doc, newDecorations), this.appliedWidgetAt);
      }
      return new NodeControlState(this.nodePos, this.nodeStart, this.currentNodeElement, decorations.remove(selectionDecorations), this.appliedWidgetAt);
    }
    return this;
  }
  applyDecorations(tr, state) {
    return this.applyWidgetDecoration(state).applySelectionDecoration(tr);
  }
}

export { NodeControlState };
//# sourceMappingURL=NodeControlState.js.map
